import React, { useState, useEffect } from 'react';
import { okrsApi, usersApi, tasksApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Target, TrendingUp, ChevronDown, ChevronRight,
  Trash2, Info, Sparkles, Building2, Users, User, Edit2, Save, X, CheckSquare
} from 'lucide-react';
import { toast } from 'sonner';

const FOX_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/28ykjv9m_Transparent%20BG%20Fox.png';

const getCurrentQuarter = () => {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `Q${q} ${now.getFullYear()}`;
};

const scoreColor = (score) => {
  if (score >= 0.7) return '#37C563';
  if (score >= 0.4) return '#FFD166';
  return '#EF4444';
};

const scoreLabel = (score) => {
  if (score >= 0.7) return 'On Track';
  if (score >= 0.4) return 'Needs Attention';
  if (score > 0) return 'At Risk';
  return 'Not Started';
};

const levelIcons = { company: Building2, team: Users, personal: User };
const levelColors = { company: '#1A2B5E', team: '#106EFD', personal: '#40CFDE' };

// OKR Guide component
const OKRGuide = ({ onClose }) => (
  <Card className="border-[#40CFDE]/30 bg-gradient-to-br from-[#F5F8FF] to-white">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <CardTitle className="text-xl font-['Amaranth'] flex items-center gap-2">
          <Info className="w-5 h-5 text-[#40CFDE]" /> What are OKRs?
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
      </div>
    </CardHeader>
    <CardContent className="space-y-4 text-sm text-navy/70">
      <div>
        <p className="font-semibold text-[#1A2B5E] text-base">OKR = Objectives and Key Results</p>
        <p className="mt-1">A goal-setting framework used by Google, Intel, and the world's top organizations to set ambitious goals with measurable outcomes.</p>
      </div>

      <div className="bg-white rounded-lg p-4 border border-navy/10">
        <p className="font-medium text-[#1A2B5E] mb-2">The Formula:</p>
        <p className="text-lg font-['Amaranth'] text-[#106EFD]">
          "I will <span className="underline decoration-[#40CFDE]">(Objective)</span> as measured by <span className="underline decoration-[#E171EA]">(Key Results)</span>"
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[#106EFD]/5 rounded-lg p-3">
          <p className="font-semibold text-[#106EFD]"><Target className="w-4 h-4 inline mr-1" /> Objectives</p>
          <ul className="mt-2 space-y-1 text-xs">
            <li>What you want to achieve</li>
            <li>Significant and action-oriented</li>
            <li>Inspirational and clear</li>
          </ul>
        </div>
        <div className="bg-[#E171EA]/5 rounded-lg p-3">
          <p className="font-semibold text-[#E171EA]"><TrendingUp className="w-4 h-4 inline mr-1" /> Key Results</p>
          <ul className="mt-2 space-y-1 text-xs">
            <li>How you measure progress</li>
            <li>Specific and time-bound</li>
            <li>Measurable: yes/no, no gray area</li>
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-lg p-4 border border-navy/10">
        <p className="font-medium text-[#1A2B5E] mb-2">Example OKR:</p>
        <div className="space-y-2">
          <p className="font-semibold text-[#106EFD]"><Target className="w-4 h-4 inline mr-1" /> Build a world-class compliance consulting team</p>
          <div className="ml-6 space-y-1 text-xs">
            <p>KR1: Achieve 95% client satisfaction score</p>
            <p>KR2: Reduce average audit turnaround from 30 to 15 days</p>
            <p>KR3: Hire 3 senior compliance specialists by Q3</p>
          </div>
        </div>
      </div>

      <div>
        <p className="font-semibold text-[#1A2B5E] mb-2">Scoring (Google Method):</p>
        <div className="flex gap-4">
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#37C563]" /><span className="text-xs">0.7-1.0 = On Track (Green)</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#FFD166]" /><span className="text-xs">0.4-0.69 = Needs Attention</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#EF4444]" /><span className="text-xs">0.0-0.39 = At Risk (Red)</span></div>
        </div>
      </div>

      <div className="bg-[#FFD166]/10 rounded-lg p-3">
        <p className="font-medium text-[#1A2B5E] text-xs">F.A.C.T.S. — The 5 Benefits of OKRs:</p>
        <div className="grid grid-cols-5 gap-2 mt-2 text-xs text-center">
          <div><span className="font-bold text-[#106EFD]">F</span>ocus</div>
          <div><span className="font-bold text-[#40CFDE]">A</span>lignment</div>
          <div><span className="font-bold text-[#E171EA]">C</span>ommitment</div>
          <div><span className="font-bold text-[#FF801D]">T</span>racking</div>
          <div><span className="font-bold text-[#37C563]">S</span>tretching</div>
        </div>
      </div>

      <p className="text-xs text-navy/50 italic">Learn more at whatmatters.com — the definitive OKR resource by John Doerr</p>
    </CardContent>
  </Card>
);

// Single OKR Card
const OKRCard = ({ okr, users, allTasks, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editKRs, setEditKRs] = useState(okr.key_results || []);
  const [editLinkedTasks, setEditLinkedTasks] = useState(okr.linked_task_ids || []);
  const LevelIcon = levelIcons[okr.level] || Target;
  const owner = users.find(u => u.id === okr.owner_id);

  const linkedTasks = allTasks.filter(t => (okr.linked_task_ids || []).includes(t.id));
  const linkedDone = linkedTasks.filter(t => t.status === 'done').length;
  const linkedTotal = linkedTasks.length;

  const handleSaveKRs = async () => {
    await onUpdate(okr.id, { key_results: editKRs, linked_task_ids: editLinkedTasks });
    setEditing(false);
  };

  const avgScore = okr.score || 0;

  return (
    <Card className="border-navy/10 overflow-hidden" data-testid={`okr-card-${okr.id}`}>
      {/* Score bar at top */}
      <div className="h-1.5" style={{ background: `linear-gradient(to right, ${scoreColor(avgScore)} ${avgScore * 100}%, #E5E7EB ${avgScore * 100}%)` }} />

      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${levelColors[okr.level]}10` }}>
            <LevelIcon className="w-5 h-5" style={{ color: levelColors[okr.level] }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1">
                {expanded ? <ChevronDown className="w-4 h-4 text-navy/40" /> : <ChevronRight className="w-4 h-4 text-navy/40" />}
                <h3 className="font-semibold text-[#1A2B5E] text-base">{okr.title}</h3>
              </button>
            </div>
            {okr.description && <p className="text-sm text-navy/60 mt-1">{okr.description}</p>}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <Badge className="text-xs" style={{ backgroundColor: `${levelColors[okr.level]}15`, color: levelColors[okr.level] }}>{okr.level}</Badge>
              <span className="text-xs text-navy/50">{okr.quarter}</span>
              {owner && <span className="text-xs text-navy/50">{owner.name}</span>}
              <div className="flex items-center gap-1">
                <span className="text-sm font-bold" style={{ color: scoreColor(avgScore) }}>{avgScore.toFixed(2)}</span>
                <span className="text-xs text-navy/40">— {scoreLabel(avgScore)}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(!editing); setEditKRs(okr.key_results || []); setEditLinkedTasks(okr.linked_task_ids || []); }}>
              <Edit2 className="w-4 h-4 text-navy/40" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDelete(okr.id)}>
              <Trash2 className="w-4 h-4 text-navy/40 hover:text-red-500" />
            </Button>
          </div>
        </div>

        {/* Key Results */}
        {expanded && (
          <div className="mt-4 ml-13 space-y-3">
            {(editing ? editKRs : (okr.key_results || [])).map((kr, idx) => (
              <div key={kr.id || idx} className="flex items-center gap-3">
                <div className="w-6 text-center text-xs font-bold text-navy/40">KR{idx + 1}</div>
                <div className="flex-1">
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <Input value={kr.title} onChange={e => { const copy = [...editKRs]; copy[idx] = {...copy[idx], title: e.target.value}; setEditKRs(copy); }} className="h-8 text-sm flex-1" />
                      <Input type="number" value={kr.current_value} onChange={e => { const copy = [...editKRs]; copy[idx] = {...copy[idx], current_value: parseFloat(e.target.value) || 0}; setEditKRs(copy); }} className="h-8 text-sm w-20" />
                      <span className="text-xs text-navy/40">/</span>
                      <Input type="number" value={kr.target_value} onChange={e => { const copy = [...editKRs]; copy[idx] = {...copy[idx], target_value: parseFloat(e.target.value) || 100}; setEditKRs(copy); }} className="h-8 text-sm w-20" />
                      <span className="text-xs text-navy/40">{kr.unit}</span>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#1A2B5E]">{kr.title}</span>
                        <span className="text-xs font-medium" style={{ color: scoreColor(kr.score || 0) }}>
                          {kr.current_value}/{kr.target_value} {kr.unit} ({((kr.score || 0) * 100).toFixed(0)}%)
                        </span>
                      </div>
                      <div className="mt-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(100, (kr.score || 0) * 100)}%`,
                            backgroundColor: scoreColor(kr.score || 0)
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {editing && (
              <div className="flex gap-2 mt-2 ml-9">
                <Button size="sm" variant="outline" onClick={() => setEditKRs([...editKRs, { id: '', title: '', target_value: 100, current_value: 0, unit: '%', score: 0 }])}>
                  <Plus className="w-3 h-3 mr-1" /> Add KR
                </Button>
                <Button size="sm" onClick={handleSaveKRs} className="bg-[#40CFDE] text-[#1A2B5E]">
                  <Save className="w-3 h-3 mr-1" /> Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            )}

            {/* Linked Tasks */}
            {editing ? (
              <div className="mt-4 ml-9 space-y-2">
                <Label className="text-sm font-medium">Linked Tasks</Label>
                <div className="flex flex-wrap gap-2">
                  {editLinkedTasks.map(tid => {
                    const t = allTasks.find(x => x.id === tid);
                    return t ? (
                      <Badge key={tid} className="bg-navy/5 text-navy/70 flex items-center gap-1">
                        <CheckSquare className="w-3 h-3" /> {t.title}
                        <button onClick={() => setEditLinkedTasks(editLinkedTasks.filter(id => id !== tid))} className="ml-1 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ) : null;
                  })}
                </div>
                <Select value="" onValueChange={v => { if (v && !editLinkedTasks.includes(v)) setEditLinkedTasks([...editLinkedTasks, v]); }}>
                  <SelectTrigger className="w-[300px] h-8"><SelectValue placeholder="+ Link a task..." /></SelectTrigger>
                  <SelectContent>
                    {allTasks.filter(t => !editLinkedTasks.includes(t.id)).map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${t.status === 'done' ? 'bg-[#37C563]' : t.status === 'in_progress' ? 'bg-[#106EFD]' : 'bg-navy/30'}`} />
                          {t.title}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : linkedTotal > 0 ? (
              <div className="mt-3 ml-9">
                <div className="flex items-center gap-2 text-xs text-navy/50 mb-2">
                  <CheckSquare className="w-3 h-3" />
                  <span>{linkedDone}/{linkedTotal} tasks completed</span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[120px]">
                    <div className="h-full bg-[#37C563] rounded-full" style={{ width: `${linkedTotal > 0 ? (linkedDone/linkedTotal)*100 : 0}%` }} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {linkedTasks.map(t => (
                    <Badge key={t.id} variant="outline" className={`text-xs ${t.status === 'done' ? 'bg-[#37C563]/10 text-[#37C563] line-through' : ''}`}>
                      {t.title}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const OKRs = () => {
  const { user: currentUser } = useAuth();
  const [okrs, setOkrs] = useState([]);
  const [users, setUsers] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [levelFilter, setLevelFilter] = useState('all');
  const [quarterFilter, setQuarterFilter] = useState(getCurrentQuarter());
  const [newOKR, setNewOKR] = useState({
    title: '', description: '', level: 'company', quarter: getCurrentQuarter(),
    year: new Date().getFullYear(), linked_task_ids: [], key_results: [
      { title: '', target_value: 100, current_value: 0, unit: '%', score: 0 },
      { title: '', target_value: 100, current_value: 0, unit: '%', score: 0 },
      { title: '', target_value: 100, current_value: 0, unit: '%', score: 0 },
    ]
  });

  const fetchData = async () => {
    try {
      const params = {};
      if (levelFilter !== 'all') params.level = levelFilter;
      if (quarterFilter !== 'all') params.quarter = quarterFilter;
      const [okrsRes, usersRes, tasksRes] = await Promise.all([okrsApi.getAll(params), usersApi.getAll(), tasksApi.getAll({})]);
      setOkrs(okrsRes.data);
      setUsers(usersRes.data);
      setAllTasks(tasksRes.data);
    } catch { toast.error('Failed to fetch OKRs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [levelFilter, quarterFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const filtered = newOKR.key_results.filter(kr => kr.title.trim());
    if (filtered.length === 0) { toast.error('Add at least one Key Result'); return; }
    try {
      await okrsApi.create({ ...newOKR, key_results: filtered });
      toast.success('OKR created!');
      setDialogOpen(false);
      setNewOKR({
        title: '', description: '', level: 'company', quarter: getCurrentQuarter(),
        year: new Date().getFullYear(), linked_task_ids: [], key_results: [
          { title: '', target_value: 100, current_value: 0, unit: '%', score: 0 },
          { title: '', target_value: 100, current_value: 0, unit: '%', score: 0 },
          { title: '', target_value: 100, current_value: 0, unit: '%', score: 0 },
        ]
      });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create OKR'); }
  };

  const handleUpdate = async (okrId, data) => {
    try {
      await okrsApi.update(okrId, data);
      toast.success('OKR updated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (okrId) => {
    try {
      await okrsApi.delete(okrId);
      toast.success('OKR deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const updateKR = (idx, field, value) => {
    const copy = [...newOKR.key_results];
    copy[idx] = { ...copy[idx], [field]: value };
    setNewOKR({ ...newOKR, key_results: copy });
  };

  const companyOKRs = okrs.filter(o => o.level === 'company');
  const teamOKRs = okrs.filter(o => o.level === 'team');
  const personalOKRs = okrs.filter(o => o.level === 'personal');
  const avgScore = okrs.length > 0 ? okrs.reduce((sum, o) => sum + (o.score || 0), 0) / okrs.length : 0;

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 loading-spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="okrs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">OKRs</h1>
          <p className="text-navy/60 mt-1">Objectives & Key Results — set ambitious goals, measure what matters</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={showGuide ? 'secondary' : 'outline'} size="sm" onClick={() => setShowGuide(!showGuide)} data-testid="okr-guide-btn">
            <Info className="w-4 h-4 mr-1" /> OKR Guide
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="create-okr-btn">
                <Plus className="w-4 h-4 mr-2" /> New OKR
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-['Amaranth']">Create OKR</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="bg-[#40CFDE]/5 rounded-lg p-3 text-sm text-navy/70">
                  <Sparkles className="w-4 h-4 inline mr-1 text-[#40CFDE]" />
                  <strong>Formula:</strong> "I will <em>(Objective)</em> as measured by <em>(Key Results)</em>"
                </div>
                <div className="space-y-2">
                  <Label>Objective — What do you want to achieve? *</Label>
                  <Input value={newOKR.title} onChange={e => setNewOKR({...newOKR, title: e.target.value})} placeholder="e.g., Build a world-class compliance consulting team" required data-testid="okr-title-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={newOKR.description} onChange={e => setNewOKR({...newOKR, description: e.target.value})} placeholder="Why does this objective matter?" rows={2} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label>Level</Label>
                    <Select value={newOKR.level} onValueChange={v => setNewOKR({...newOKR, level: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                      <SelectItem value="company">Company</SelectItem><SelectItem value="team">Team</SelectItem><SelectItem value="personal">Personal</SelectItem>
                    </SelectContent></Select></div>
                  <div className="space-y-2"><Label>Quarter</Label>
                    <Select value={newOKR.quarter} onValueChange={v => setNewOKR({...newOKR, quarter: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                      {['Q1 2026','Q2 2026','Q3 2026','Q4 2026'].map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                    </SelectContent></Select></div>
                  <div className="space-y-2"><Label>Owner</Label>
                    <Select value={newOKR.owner_id || ''} onValueChange={v => setNewOKR({...newOKR, owner_id: v})}><SelectTrigger><SelectValue placeholder="Me" /></SelectTrigger><SelectContent>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent></Select></div>
                </div>

                <div className="space-y-3">
                  <Label>Key Results — How will you measure progress?</Label>
                  {newOKR.key_results.map((kr, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-xs font-bold text-navy/40 w-8">KR{idx + 1}</span>
                      <Input value={kr.title} onChange={e => updateKR(idx, 'title', e.target.value)} placeholder={`Key Result ${idx + 1}`} className="flex-1" data-testid={`kr-title-${idx}`} />
                      <Input type="number" value={kr.target_value} onChange={e => updateKR(idx, 'target_value', parseFloat(e.target.value) || 100)} className="w-20" />
                      <Select value={kr.unit} onValueChange={v => updateKR(idx, 'unit', v)}>
                        <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="%">%</SelectItem>
                          <SelectItem value="count">#</SelectItem>
                          <SelectItem value="days">days</SelectItem>
                          <SelectItem value="score">score</SelectItem>
                        </SelectContent>
                      </Select>
                      {newOKR.key_results.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setNewOKR({...newOKR, key_results: newOKR.key_results.filter((_, i) => i !== idx)})}>
                          <Trash2 className="w-4 h-4 text-navy/30" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {newOKR.key_results.length < 5 && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setNewOKR({...newOKR, key_results: [...newOKR.key_results, { title: '', target_value: 100, current_value: 0, unit: '%', score: 0 }]})}>
                      <Plus className="w-3 h-3 mr-1" /> Add Key Result
                    </Button>
                  )}
                </div>

                {/* Link Tasks */}
                <div className="space-y-2">
                  <Label>Link Tasks (optional)</Label>
                  <p className="text-xs text-navy/50">Linked task completion can track progress toward Key Results</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {newOKR.linked_task_ids.map(tid => {
                      const t = allTasks.find(x => x.id === tid);
                      return t ? (
                        <Badge key={tid} className="bg-navy/5 text-navy/70 flex items-center gap-1">
                          <CheckSquare className="w-3 h-3" /> {t.title}
                          <button type="button" onClick={() => setNewOKR({...newOKR, linked_task_ids: newOKR.linked_task_ids.filter(id => id !== tid)})} className="ml-1 hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                  <Select value="" onValueChange={v => { if (v && !newOKR.linked_task_ids.includes(v)) setNewOKR({...newOKR, linked_task_ids: [...newOKR.linked_task_ids, v]}); }}>
                    <SelectTrigger className="w-full h-8" data-testid="link-task-select"><SelectValue placeholder="+ Link a task..." /></SelectTrigger>
                    <SelectContent>
                      {allTasks.filter(t => !newOKR.linked_task_ids.includes(t.id)).map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E]" data-testid="submit-okr-btn"><Target className="w-4 h-4 mr-2" /> Create OKR</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Guide */}
      {showGuide && <OKRGuide onClose={() => setShowGuide(false)} />}

      {/* Score Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-navy/10"><CardContent className="p-5">
          <p className="text-sm text-navy/60">Total OKRs</p>
          <p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth'] mt-1">{okrs.length}</p>
        </CardContent></Card>
        <Card className="border-navy/10"><CardContent className="p-5">
          <p className="text-sm text-navy/60">Avg Score</p>
          <p className="text-3xl font-bold font-['Amaranth'] mt-1" style={{ color: scoreColor(avgScore) }}>{avgScore.toFixed(2)}</p>
        </CardContent></Card>
        <Card className="border-navy/10"><CardContent className="p-5">
          <p className="text-sm text-navy/60">On Track</p>
          <p className="text-3xl font-bold text-[#37C563] font-['Amaranth'] mt-1">{okrs.filter(o => (o.score || 0) >= 0.7).length}</p>
        </CardContent></Card>
        <Card className="border-navy/10"><CardContent className="p-5">
          <p className="text-sm text-navy/60">At Risk</p>
          <p className="text-3xl font-bold text-[#EF4444] font-['Amaranth'] mt-1">{okrs.filter(o => (o.score || 0) < 0.4 && (o.score || 0) > 0).length}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-[150px]" data-testid="okr-level-filter"><SelectValue placeholder="All Levels" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="company">Company</SelectItem>
            <SelectItem value="team">Team</SelectItem>
            <SelectItem value="personal">Personal</SelectItem>
          </SelectContent>
        </Select>
        <Select value={quarterFilter} onValueChange={setQuarterFilter}>
          <SelectTrigger className="w-[150px]" data-testid="okr-quarter-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Quarters</SelectItem>
            {['Q1 2026','Q2 2026','Q3 2026','Q4 2026'].map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* OKRs by level */}
      {okrs.length === 0 ? (
        <Card className="border-navy/10">
          <CardContent className="py-16 text-center">
            <img src={FOX_MASCOT} alt="Fox" className="w-20 h-20 mx-auto mb-4" />
            <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">No OKRs yet</h3>
            <p className="text-navy/50">Set your first Objective to start measuring what matters</p>
            <Button className="mt-4 bg-[#40CFDE] text-[#1A2B5E]" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Create Your First OKR
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {companyOKRs.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-[#1A2B5E] font-['Amaranth'] mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Company OKRs
              </h2>
              <div className="space-y-3">
                {companyOKRs.map(okr => <OKRCard key={okr.id} okr={okr} users={users} allTasks={allTasks} onUpdate={handleUpdate} onDelete={handleDelete} />)}
              </div>
            </div>
          )}
          {teamOKRs.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-[#1A2B5E] font-['Amaranth'] mb-3 flex items-center gap-2">
                <Users className="w-5 h-5" /> Team OKRs
              </h2>
              <div className="space-y-3">
                {teamOKRs.map(okr => <OKRCard key={okr.id} okr={okr} users={users} allTasks={allTasks} onUpdate={handleUpdate} onDelete={handleDelete} />)}
              </div>
            </div>
          )}
          {personalOKRs.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-[#1A2B5E] font-['Amaranth'] mb-3 flex items-center gap-2">
                <User className="w-5 h-5" /> Personal OKRs
              </h2>
              <div className="space-y-3">
                {personalOKRs.map(okr => <OKRCard key={okr.id} okr={okr} users={users} allTasks={allTasks} onUpdate={handleUpdate} onDelete={handleDelete} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OKRs;
